import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDateLocale } from '../../../../utils/formatDateLocale';
import { isDateWithinSevenDays } from '../../../../utils/date';
import { getAttendanceSummaryByMeetId } from '../../../../service/attendance';
import { Meet as MeetModel } from '../../../../models/meet';
import { Book } from '../Book/Book';
import Icon from '../../../../components/Icon/Icon';
import { main_color } from '../../../../styles/_variables';
import styles from './Meet.module.scss';
import {
  LinkOutlined,
  FileTextOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { getClubBookReviewByMeet } from '../../../../service/clubBookReview';

interface Props{
  meet: MeetModel;
}

const Meet = ({meet}: Props) => {
  const navigate = useNavigate();
  const { id, name: clubName } = useParams();
  const clubId = Number(id);
  const meetId = meet.id;

  const [details, setDetails] = useState(null);
  const [generalStarsRating, setGeneralStarsRating] = useState('0.0');

  const {data:attendanceSummary, isFetching} = useQuery(
    ['attendanceSummary', meetId],
    () => getAttendanceSummaryByMeetId(meetId, clubId),
    {refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false},
  );

  const { data: clubBookReviewSummary, refetch } = useQuery(
    ['clubBookReviewSummary', meet.id],
    () => getClubBookReviewByMeet(meet.clubId, meet.id),
    { refetchOnMount: 'always', staleTime: 0, refetchOnWindowFocus: false }
  );
  
  const getDetails = () => {
    const currentDate = new Date();
    const dateMeet = new Date(meet.dateMeet);
    if (attendanceSummary?.totalMembers === 0 || attendanceSummary?.totalMembers === null){
      if (currentDate < dateMeet){
        return (
          <>
            <div>O encontro ainda não aconteceu!</div>
            <div className={styles.actionIcon}>
              <button className={styles.blockButton} title="Aguarde!" disabled>
                <Icon icon="waiting" color={main_color} />
              </button>
            </div>
          </>
        );
      } 
      
      if (!isDateWithinSevenDays(attendanceSummary?.dateMeet)){
        return (
          <>
            <div>Não foram registradas as presenças para este encontro. <br /> Prazo expirado!</div>
            <div className={styles.actionIcon}>
              <button className={styles.blockButton} title="Prazo expirado!" disabled>
                <Icon icon="clipboard-block" color={main_color} />
              </button>
            </div>
          </>
        );
      }

      return (
        <>
          <div>Ainda não foram registradas as presenças do encontro!</div>
          <div className={styles.actionIcon}>
            <button 
              className={styles.editButton} title="Registrar presenças!"
              onClick={() => navigate(`/club/${clubId}/${clubName}/attendances/meet/${meetId}/register`)}
            >
              <Icon icon="clipboard-plus" color={main_color} />
            </button>
          </div>
        </>
      );
    } 

    return (
      <>
        <div className={styles.textIcon}>
          <Icon icon="group" className={styles.icon} color={main_color} />
          <span>{attendanceSummary?.membersWasInAttendance}/{attendanceSummary?.totalMembers} clubistas presentes no encontro</span>
        </div>
        <div className={styles.actionIcon}>
          <button 
            className={styles.editButton} title="Visualizar registros!"
            onClick={() => navigate(`/club/${clubId}/${clubName}/attendances/meet/${meetId}`)}
          >
            <Icon icon="clipboard-check" color={main_color} />
          </button>
        </div>
      </>
    );
  }

  useEffect(() => {
    const details_ = getDetails();
    setDetails(details_);
  }, [attendanceSummary]);

  useEffect(() => {
    if(clubBookReviewSummary){
      if (typeof clubBookReviewSummary.averageRating === 'number') {
        setGeneralStarsRating(clubBookReviewSummary?.averageRating.toFixed(1));
      }
    }
  },[clubBookReviewSummary]);

  if(isFetching) return null;

  return (
    <div className={styles.item} key={"meet_"+meetId}>
      <div className={styles.meetContainer}>
        <div className={styles.meetBook}>
          <Book
            key={meet.bookId} 
            bookId={meet.bookId}/>
        </div>
        <div className={styles.meetInfo}>
          <h1><strong>{formatDateLocale(meet.dateMeet)}</strong></h1>
          {meet?.link && 
            <>
              <br/>
              <h2><strong><LinkOutlined /> Link:</strong> <span>{meet?.link || ''}</span></h2>
            </>
          }
          {meet?.location && 
            <>
              <br/>
              <h2><strong><EnvironmentOutlined /> Local:</strong> <span>{meet?.location || ''}</span></h2>
            </>
          }
          {meet?.notes && 
            <>
              <br/>
              <h2><strong><FileTextOutlined /> Notas:</strong> <span>{meet?.notes || ''}</span></h2>
            </>
          }
        </div>
      </div>
      <div className={styles.detailsField}>
        <div className={styles.bottom}>
          {details}
        </div>
        <div className={styles.rating}>
          <div className={styles.boxRating} title="Avaliação Geral">
            {generalStarsRating}
          </div>
          <div className={styles.starsReader}>
            <span>
              {clubBookReviewSummary?.totalReaders} leitores concluiram
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Meet;